import { DestroyRef, inject, Injectable } from '@angular/core';
import { createStore } from '@ngneat/elf';
import {
  addEntities,
  deleteAllEntities,
  deleteEntities,
  hasEntity,
  selectAllEntities,
  withEntities,
} from '@ngneat/elf-entities';
import { persistState } from '@ngneat/elf-persist-state';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { createIdbStorage } from './persistence/idb-storage';
import { tenantStorageKey } from '../shared/helpers/tenant';

// One raw join/leave event. The activity log is a flat per-event stream;
// roster aggregation (one row per user, one row per (user × breakout))
// happens at read time via `aggregateAttendance` in `activity-log.aggregate.ts`.
export type ActivityLogEvent = {
  id: string;
  time: Date;
  // The room the activity happened in (sourced from `UserSettingsStore.roomName`
  // at the moment of recording). Consumers filter by this to scope the log to
  // the currently joined room.
  roomId: string;
  userId: string;
  name: string;
  avatar: string;
  kind: 'joined' | 'left';
  scope: 'room' | 'breakout';
  // Present iff `scope === 'breakout'`: the breakout room name that was
  // joined or left.
  breakoutRoom?: string;
};

export type ActivityLogEventDraft = Omit<ActivityLogEvent, 'id'> & { id?: string };

// v2 dropped the multi-user grouped-entry shape in favour of a flat per-event
// stream. Bumping the name discards any v1 entries silently on rehydration —
// safe because the TTL was 2 days anyway.
const ACTIVITY_LOG_STORE_NAME = 'andorActivityLog.v2';

// Events older than this are evicted on rehydration and on every add. IDB
// has no native TTL — checking on the user-driven mutation path is enough
// because the only way stale events can be observed is via the store and
// any consumer subscribing to the store causes at least one prune by the
// time something is rendered.
const ACTIVITY_LOG_TTL_MS = 2 * 24 * 60 * 60 * 1000;

const idbStorage = createIdbStorage('andor');

const activityLogStore = createStore(
  { name: ACTIVITY_LOG_STORE_NAME },
  withEntities<ActivityLogEvent>()
);

@Injectable({ providedIn: 'root' })
export class ActivityLogStore {
  store = activityLogStore;
  events$ = activityLogStore.pipe(selectAllEntities());
  // Mirrors MessageStore.initialized$ — fires `true` once IDB rehydration
  // has completed. Consumers that read `store.getValue()` at startup must
  // wait on this.
  initialized$ = new BehaviorSubject<boolean>(false);

  constructor() {
    const handle = persistState(activityLogStore, {
      key: tenantStorageKey(ACTIVITY_LOG_STORE_NAME),
      storage: idbStorage,
    });
    // The store is module-scoped and outlives this instance. Without this,
    // every re-created injector (each TestBed spec) stacks another IDB writer
    // onto it, and each one clones the whole log on every update.
    inject(DestroyRef).onDestroy(() => handle.unsubscribe());
    handle.initialized$.subscribe((ready) => {
      if (ready) this._pruneExpired();
      this.initialized$.next(ready);
    });
  }

  eventsForRoom$(roomId: string): Observable<ActivityLogEvent[]> {
    return this.events$.pipe(map((events) => events.filter((e) => e.roomId === roomId)));
  }

  has(id: string): boolean {
    return activityLogStore.query(hasEntity(id));
  }

  add(draft: ActivityLogEventDraft): ActivityLogEvent {
    this._pruneExpired();
    const event: ActivityLogEvent = { ...draft, id: draft.id ?? crypto.randomUUID() };
    activityLogStore.update(addEntities(event));
    return event;
  }

  delete(id: string): void {
    activityLogStore.update(deleteEntities(id));
  }

  clear(): void {
    activityLogStore.update(deleteAllEntities());
  }

  private _pruneExpired(): void {
    const cutoff = Date.now() - ACTIVITY_LOG_TTL_MS;
    const state = activityLogStore.getValue();
    const toDelete: string[] = [];
    for (const id of state.ids as string[]) {
      const event = state.entities[id];
      if (event && event.time.getTime() < cutoff) toDelete.push(id);
    }
    if (toDelete.length > 0) activityLogStore.update(deleteEntities(toDelete));
  }
}
